import { Link } from "react-router-dom";

/**
 * Site-wide footer.
 *
 * Five product/marketing columns + a "Stay in touch" block + a legal-entity
 * row at the bottom (Agent 2 added the entity block; we preserve it here).
 *
 * Buyer surfaces every page that operators care about, so nothing the prior
 * agents added is hidden from sales prospects.
 *
 * Responsive: the column grid uses `auto-fit minmax(180px, 1fr)`, which
 * collapses to a single column under ~360px without any media queries.
 *
 * The legal column previously carried a "Cookie preferences" button that
 * reset stored consent. The cookie-banner subsystem was removed when the
 * studio dropped its third-party sub-processors (no analytics, no
 * marketing pixels), so we no longer set non-essential cookies and there
 * is nothing for a cookie-preferences control to toggle. The button was
 * removed alongside `lib/consent.ts`.
 */

type LinkItem = { label: string; to: string; external?: boolean };

const COL_PRODUCT: LinkItem[] = [
  { label: "Games", to: "/games" },
  { label: "Integration", to: "/integration" },
  { label: "API reference", to: "/api-reference" },
  { label: "SDK", to: "/sdk" },
  { label: "Webhooks", to: "/webhooks" },
  { label: "Sandbox", to: "/sandbox" },
];

const COL_OPERATORS: LinkItem[] = [
  { label: "Operator console", to: "/operator" },
  { label: "Status", to: "/status" },
  { label: "Roadmap", to: "/roadmap" },
  // /transparency/:operatorId is the canonical route — link a representative
  // example so buyers can see what the per-operator transparency page looks
  // like without us having to publish a real operator's data.
  { label: "Transparency", to: "/transparency/demo" },
  { label: "Security", to: "/security" },
];

const COL_COMPANY: LinkItem[] = [
  { label: "About", to: "/about" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
  {
    label: "Press",
    to: "mailto:hello@vaultbreaker.dev?subject=Press%20enquiry",
    external: true,
  },
  // TODO: replace with /careers route once we publish a real careers page.
  {
    label: "Careers",
    to: "mailto:hello@vaultbreaker.dev?subject=Careers%20enquiry",
    external: true,
  },
];

const COL_RESOURCES: LinkItem[] = [
  { label: "Docs", to: "/docs" },
  { label: "FAQ", to: "/faq" },
  { label: "Responsible gaming", to: "/responsible-gaming" },
  // Anchor link inside /responsible-gaming. The id may not exist yet — when
  // /responsible-gaming gets an `id="helplines"` block this will resolve;
  // until then the browser lands at the top of the page, which is fine.
  { label: "Helplines", to: "/responsible-gaming#helplines" },
];

// Legal column is now a plain link list — the cookie-preferences button
// was removed when the cookie-consent subsystem was retired.
const COL_LEGAL: LinkItem[] = [
  { label: "Privacy", to: "/legal/privacy" },
  { label: "Terms", to: "/legal/terms" },
];

// TODO: real social handles — these placeholders match the Organization
// schema in seo.tsx, which already references the same URLs.
const SOCIAL_LINKS: LinkItem[] = [
  { label: "X (Twitter)", to: "https://twitter.com/vaultbreakerstudios", external: true },
  { label: "LinkedIn", to: "https://www.linkedin.com/company/vault-breaker-studios", external: true },
];

export function SiteFooter() {
  return (
    <footer
      style={{
        borderTop: "1px solid var(--line)",
        background: "var(--bg-1)",
        marginTop: 80,
        padding: "56px 20px 28px",
        position: "relative",
        zIndex: 4,
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gap: 32,
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            marginBottom: 40,
          }}
        >
          {/* Brand block */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <img src="/logo-mark.svg" alt="" style={{ height: 24, width: 24 }} />
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 15,
                  letterSpacing: "0.06em",
                  color: "var(--fg-1)",
                }}
              >
                VAULT BREAKER STUDIOS
              </span>
            </div>
            <p style={{ fontSize: 12, color: "var(--fg-3)", margin: 0, lineHeight: 1.6 }}>
              New B2B game studio. Free to integrate · 10% of GGR · provably fair · no player custody.
            </p>

            {/* Social links — hidden behind a tiny block under the description
                so they don't compete with the column nav. */}
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: "16px 0 0",
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              {SOCIAL_LINKS.map((s) => (
                <li key={s.to}>
                  <a
                    href={s.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 11,
                      fontFamily: "var(--font-mono)",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--fg-3)",
                      textDecoration: "none",
                    }}
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <FooterCol heading="Product" items={COL_PRODUCT} />
          <FooterCol heading="Operators" items={COL_OPERATORS} />
          <FooterCol heading="Company" items={COL_COMPANY} />
          <FooterCol heading="Resources" items={COL_RESOURCES} />
          <FooterCol heading="Legal" items={COL_LEGAL} />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            paddingTop: 20,
            borderTop: "1px solid var(--line)",
            fontSize: 11,
            color: "var(--fg-3)",
            fontFamily: "var(--font-mono)",
          }}
        >
          <span>© 2026 Vault Breaker Studios. B2B platform — 21+ at operator site.</span>
          <span>No player custody · licensing sits with the operator</span>
        </div>

        {/* TODO: replace with real entity once incorporated */}
        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: "1px solid var(--line)",
            fontSize: 11,
            color: "var(--fg-3)",
            fontFamily: "var(--font-mono)",
            lineHeight: 1.6,
          }}
        >
          © 2026 Vault Breaker Studios. Vault Breaker Studios Ltd (incorporation in progress) —
          registered office and entity number to be published prior to first signed pilot.{" "}
          <a
            href="mailto:hello@vaultbreaker.dev"
            style={{ color: "var(--fg-2)", textDecoration: "none" }}
          >
            hello@vaultbreaker.dev
          </a>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ heading, items }: { heading: string; items: LinkItem[] }) {
  return (
    <div>
      <span className="label" style={{ marginBottom: 10, display: "block" }}>
        {heading}
      </span>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
        {items.map((item) => (
          <li key={item.to}>{renderLink(item)}</li>
        ))}
      </ul>
    </div>
  );
}

function renderLink(item: LinkItem) {
  const sharedStyle = {
    fontSize: 13,
    color: "var(--fg-2)",
    textDecoration: "none",
  } as const;

  if (item.external || item.to.startsWith("mailto:") || item.to.startsWith("http")) {
    return (
      <a
        href={item.to}
        target={item.to.startsWith("mailto:") ? undefined : "_blank"}
        rel={item.to.startsWith("mailto:") ? undefined : "noopener noreferrer"}
        style={sharedStyle}
      >
        {item.label}
      </a>
    );
  }

  return (
    <Link to={item.to} style={sharedStyle}>
      {item.label}
    </Link>
  );
}
